// Tracker.tsx
import React, { useEffect, useState } from 'react';
import { Button, Typography, Spin } from 'antd';
import { ActivityCard } from './ActivityCard';
import { PipelineStage } from './PipelineStage';
import {
  leadActivityConfig,
  leadPipelineConfig,
  dealActivityConfig,
  dealPipelineConfig,
} from './dashboardConfig';
import { getData } from '../../../../../../actions/apiCall';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import { MailOutlined, LineChartOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';

const { Title } = Typography;

interface SectionHeaderProps {
  title: string;
  projectType?: string | null;
  onProjectTypeChange?: (type: string | null) => void;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, projectType, onProjectTypeChange }) => (
  <div className="flex justify-between items-center mb-4">
    <Title level={4} className="!mb-0 !text-lg font-semibold text-gray-800">
      {title}
    </Title>
    <div className="space-x-2">
      <Button
        type={projectType === 'StaffAugmentation' ? 'primary' : 'default'}
        ghost={projectType !== 'StaffAugmentation'}
        onClick={() => onProjectTypeChange && onProjectTypeChange(projectType === 'StaffAugmentation' ? null : 'StaffAugmentation')}
      >
        Contract
      </Button>
      <Button
        type={projectType === 'FixBid' ? 'primary' : 'default'}
        ghost={projectType !== 'FixBid'}
        onClick={() => onProjectTypeChange && onProjectTypeChange(projectType === 'FixBid' ? null : 'FixBid')}
      >
        Fix Bid
      </Button>
    </div>
  </div>
);

interface TrackerProps {
  bdeId?: string | null;
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;
  period?: string;
}

export const Tracker: React.FC<TrackerProps> = ({
  bdeId,
  startDate,
  endDate,
  period = 'month'
}) => {
  const [trackerData, setTrackerData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [projectType, setProjectType] = useState<string | null>(null);

  const dispatch = useDispatch();
  const { user, isGlobalAdmin } = useSelector((state: any) => state.app);
  const isAdmin = isGlobalAdmin || user?.designation === 'Admin';

  useEffect(() => {
    if (startDate && endDate && (isAdmin || bdeId)) {
      fetchTrackerData();
    }
  }, [bdeId, startDate, endDate, period, isAdmin, projectType]);

  const fetchTrackerData = async () => {
    if (!startDate || !endDate) return;
    if (!isAdmin && !bdeId) return;

    try {
      setLoading(true);
      const startDateStr = startDate.format('YYYY-MM-DD');
      const endDateStr = endDate.format('YYYY-MM-DD');
      const bdeIdParam = bdeId === 'all' || !bdeId ? 'all' : bdeId;
      const projectTypeParam = projectType ? `&projectType=${projectType}` : '';

      const res = await dispatch(
        getData(`/targets/bde/tracker-dashboard?bdeId=${bdeIdParam}&startDate=${startDateStr}&endDate=${endDateStr}&period=${period}${projectTypeParam}`)
      );

      if (res?.code === 200 && res?.data) {
        setTrackerData(res.data);
      }
    } catch (error) {
      console.error('Error fetching tracker dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map icon strings to React components
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'mail':
        return <MailOutlined />;
      case 'line-chart':
        return <LineChartOutlined />;
      case 'check-circle':
        return <CheckCircleOutlined />;
      case 'close-circle':
        return <CloseCircleOutlined />;
      default:
        return <MailOutlined />;
    }
  };

  // Use API data if available, otherwise use config defaults
  const leadActivities = trackerData?.leadActivities || leadActivityConfig;
  const leadsPipeline = trackerData?.leadsPipeline || leadPipelineConfig;
  const dealActivities = trackerData?.dealActivities || dealActivityConfig;
  const dealsPipeline = trackerData?.dealsPipeline || dealPipelineConfig;

  // Map API data to component format
  const mappedLeadActivities = leadActivities.map((item: any) => ({
    ...item,
    icon: typeof item.icon === 'string' ? getIcon(item.icon) : item.icon
  }));

  const mappedDealActivities = dealActivities.map((item: any) => ({
    ...item,
    icon: typeof item.icon === 'string' ? getIcon(item.icon) : item.icon
  }));

  return (
    <div className="min-h-screen space-y-6">
      <Title level={2} className="!mb-0 !text-xl font-bold text-gray-900">
        CRM Tracker Dashboard
      </Title>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Lead Activities */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 !m-0">
            <SectionHeader
              title="Lead Activities"
              projectType={projectType}
              onProjectTypeChange={setProjectType}
            />
            <div className="flex flex-wrap gap-4 justify-between">
              {mappedLeadActivities.map((data: any, index: number) => (
                <ActivityCard key={index} data={data} />
              ))}
            </div>
          </div>

          {/* Leads Pipeline */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <SectionHeader
              title="Leads Pipeline"
              projectType={projectType}
              onProjectTypeChange={setProjectType}
            />
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <div className="flex w-fit bg-white">
                {leadsPipeline.map((data: any, index: number) => (
                  <PipelineStage key={index} data={data} />
                ))}
              </div>
            </div>
          </div>

          {/* Deal Activities */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <SectionHeader
              title="Deal Activities"
              projectType={projectType}
              onProjectTypeChange={setProjectType}
            />
            <div className="flex flex-wrap gap-4 justify-between">
              {mappedDealActivities.map((data: any, index: number) => (
                <ActivityCard key={index} data={data} />
              ))}
            </div>
          </div>

          {/* Deals Pipeline */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <SectionHeader
              title="Deals Pipeline"
              projectType={projectType}
              onProjectTypeChange={setProjectType}
            />
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <div className="flex w-fit bg-white">
                {dealsPipeline.map((data: any, index: number) => (
                  <PipelineStage key={index} data={data} />
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
